import * as stylex from '@stylexjs/stylex'
import { memo, useMemo, type ComponentPropsWithoutRef, type ElementType, type ForwardedRef, type ReactNode } from 'react'
import isEqual from 'react-fast-compare'
import { TableContext, useTableContext, type TableContextValue } from '../Table/TableContext'
import { makeExtendStyle, type ExtendStyleProps } from '../utils/makeExtendStyle/makeExtendStyle'

const _baseStyles = stylex.create({
  root: {
    display: 'table-header-group',
  },
})

export type TableHeadProps<RootComponent extends ElementType = 'thead', TableHeadElement extends HTMLElement = HTMLTableSectionElement> = {
  /** 自訂根元素標籤 */
  'component'?: RootComponent
  /** 子內容（通常為 TableRow） */
  'children'?: ReactNode
  /** 自訂根元素的 StyleX 樣式 */
  'rootStyle'?: stylex.StyleXStyles
  /** 根元素的 ref */
  'rootRef'?: ForwardedRef<TableHeadElement>
  /** 測試用 data 屬性 */
  'data-testid'?: string
} & ExtendStyleProps & ComponentPropsWithoutRef<RootComponent>

const TableHeadComponent = <RootComponent extends ElementType = 'thead', TableHeadElement extends HTMLElement = HTMLTableSectionElement>(
  props: TableHeadProps<RootComponent, TableHeadElement>,
) => {
  const { component = 'thead', extendStyles, rootStyle, rootRef, ...rest } = makeExtendStyle(props)
  const Component = component as ElementType
  const defaultComponent = 'thead'
  const parentTableContext = useTableContext()

  const tableContextValue: TableContextValue = useMemo(
    () => ({
      ...parentTableContext,
      variant: 'head',
    }),
    [parentTableContext],
  )

  return (
    <TableContext.Provider value={tableContextValue}>
      <Component
        ref={rootRef}
        role={component === defaultComponent ? undefined : 'rowgroup'}
        {...stylex.props(_baseStyles.root, rootStyle, extendStyles)}
        {...rest}
      />
    </TableContext.Provider>
  )
}

/**
 * TableHead 是表格標題區域元件，透過 context 將 variant 設為 'head'。
 */
export const TableHead = memo(TableHeadComponent, isEqual) as typeof TableHeadComponent
